import React, { useCallback, useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import Table from "../../components/datatable/datatable";
import SearchComponent from "../../components/search";
import { customerFetchPartnerSubsidieApplications } from "../../core/axios/endpoints";

// Read-only overzicht voor subsidie-partners: aanvragen (EIA / EIA Zon / ISDE)
// die via hun partner-link zijn binnengekomen. Geen PII, geen bedragen, geen
// detailpagina (contract 4 in AGENTS.md 3.28).

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");
// datumOpdrachtBevestiging is een yyyy-mm-dd string.
const fmtIsoDate = (s) => {
  if (!s || typeof s !== "string") return "-";
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}-${m}-${y}` : s;
};

// Jira-lozenges: waarschuwing / info / succes / gevaar (designbrief).
const STATUS_LABELS = {
  "In-behandeling": { label: "In behandeling", color: "bg-[#FFF7D6] text-[#7F5F01]" },
  "Ingediend-bij-rvo": { label: "Ingediend bij RVO", color: "bg-brand-subtle text-brand" },
  Goedgekeurd: { label: "Goedgekeurd", color: "bg-[#DCFFF1] text-[#1F845A]" },
  Afgekeurd: { label: "Afgekeurd", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

const INVOICE_STATUS_LABELS = {
  none: { label: "Nog niet van toepassing", color: "bg-[#091E420F] text-ink-subtle" },
  pending: { label: "Nog niet gefactureerd", color: "bg-[#F3F0FF] text-[#5E4DB2]" },
  invoiced: { label: "Gefactureerd", color: "bg-brand-subtle text-brand" },
  paid: { label: "Betaald", color: "bg-[#DCFFF1] text-[#1F845A]" },
  credited: { label: "Gecrediteerd", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

const Pill = ({ cfg }) => (
  <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${cfg.color}`}>
    {cfg.label}
  </span>
);

const PartnerSubsidieAanvragenPage = () => {
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [regling, setRegling] = useState("");

  const fetchData = useCallback(
    async ({ page, pageSize, resetPageSize }, search = searchText, reg = regling) => {
      setLoading(true);
      setError(null);
      try {
        const body = await customerFetchPartnerSubsidieApplications({
          page,
          limit: pageSize,
          search,
          regling: reg,
        });
        const payload = body?.data ?? body;
        setData({
          data: (payload?.docs ?? []).map((item, index) => ({ ...item, id: index })),
          totalDataCount: payload?.totalDocs ?? payload?.pagination?.totalDocs ?? 0,
          resetPageSize,
        });
      } catch (e) {
        console.error("Partner subsidie-lijst ophalen mislukt:", e);
        setError(
          e?.response?.data?.error ??
            "De aanvragen konden niet worden geladen. Probeer het later opnieuw.",
        );
      } finally {
        setLoading(false);
      }
    },
    [searchText, regling],
  );

  useEffect(() => {
    fetchData({ page: 1, pageSize: 25, resetPageSize: false });
  }, []);

  const handleSearch = (q) => {
    setSearchText(q);
    fetchData({ page: 1, pageSize: 25, resetPageSize: true }, q, regling);
  };

  const handleRegling = (value) => {
    setRegling(value);
    fetchData({ page: 1, pageSize: 25, resetPageSize: true }, searchText, value);
  };

  const columns = [
    {
      title: "Kenmerk",
      dataIndex: "Kenmerk",
      render: (v) => <span className="text-sm font-medium text-ink">{v || "-"}</span>,
    },
    {
      title: "Handelsnaam",
      dataIndex: "handelsNaam",
      render: (v) => <span className="font-medium text-ink">{v || "-"}</span>,
      sorter: (a, b) => (a.handelsNaam ?? "").localeCompare(b.handelsNaam ?? ""),
    },
    {
      title: "KvK",
      dataIndex: "KvkNummer",
      render: (v) => <span className="text-sm text-ink-subtle">{v || "-"}</span>,
    },
    {
      title: "Plaats",
      dataIndex: "plaats",
      render: (v) => <span className="text-sm text-ink-subtle">{v || "-"}</span>,
    },
    {
      title: "Regeling",
      dataIndex: "regling",
      render: (v) => <span className="text-sm">{v || "-"}</span>,
    },
    {
      title: "Datum opdracht",
      dataIndex: "datumOpdrachtBevestiging",
      render: (v) => <span className="text-sm">{fmtIsoDate(v)}</span>,
    },
    {
      title: "Ingediend",
      dataIndex: "createdAt",
      render: (v) => <span className="text-sm">{fmtDate(v)}</span>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (
        <Pill cfg={STATUS_LABELS[v] ?? { label: v || "-", color: "bg-[#091E420F] text-ink-subtle" }} />
      ),
    },
    {
      title: "Facturatie",
      dataIndex: "partnerInvoiceStatus",
      render: (v) => <Pill cfg={INVOICE_STATUS_LABELS[v ?? "none"] ?? INVOICE_STATUS_LABELS.none} />,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <header className="mb-6">
          <h1 className="text-2xl font-medium text-ink">Subsidie-aanvragen</h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            Overzicht van alle subsidie-aanvragen (EIA, EIA Zon, ISDE) die via uw partner-link zijn binnengekomen.
          </p>
        </header>

        {/* Toolbar: zoeken + filter */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="w-full max-w-xs">
            <SearchComponent onSearch={handleSearch} type={"partner-subsidie"} />
          </div>
          <select
            value={regling}
            onChange={(e) => handleRegling(e.target.value)}
            className="h-8 rounded border-2 border-line bg-white px-2 text-sm text-ink focus:border-brand focus:outline-none"
          >
            <option value="">Alle regelingen</option>
            <option value="EIA">EIA</option>
            <option value="EIA Zon">EIA Zon</option>
            <option value="ISDE">ISDE</option>
          </select>
          <span className="ml-auto text-xs text-ink-subtlest">
            {data?.totalDataCount ?? 0} aanvragen
          </span>
        </div>

        {error ? (
          <div className="mb-4 flex items-start gap-2 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
            <FeatherIcon icon="alert-circle" size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="table-responsive rounded bg-white shadow-card">
          <Table columns={columns} response={data} fetchData={fetchData} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default PartnerSubsidieAanvragenPage;
